import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import './SoloBotPage.css';
import { useNavigate, useLocation } from 'react-router-dom';
import latinize from 'latinize';
import TimeLimitContext from '../../context/TimeLimitContext';
import StealCapContext from '../../context/StealCapContext';
import { ToastContainer, toast } from 'react-toastify';
import { selectRandomCombination } from '../../helpers/selectRandomCombination';
import { getSquareCombinationsArg } from '../../services/api';
import { winningPositions } from '../../helpers/winningPositions';
import { checkPlayer } from '../../helpers/checkPlayer';
import { saveGameToken, savePoints } from '../../helpers/botPoints';
import { changeDifficulty, saveStreak } from '../../helpers/streakManagement';
import { pickBotSquareOrder, shouldBotShield } from '../../helpers/botMove';
import { FlameIcon } from '../../helpers/animations/flame';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../../components/Navbar/Navbar';
import AdBanner from '../../components/AdBanner/AdBanner';
import Board from '../../components/Board/Board';
import ScoreBoard from '../../components/ScoreBoard/ScoreBoard';
import NextButton from '../../components/NextButton/NextButton';
import PlayerSelected from '../../components/PlayerSelected/PlayerSelected';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import BotCharacter from '../../components/BotCharacter/BotCharacter';
import { pickFour12Character } from '../../components/BotCharacter/four12Characters';
import useClassicRival from '../../components/BotCharacter/useClassicRival';
import RivalPicker from '../../components/BotCharacter/RivalPicker';

const backendURL = (process.env.REACT_APP_BACKEND_URL || 'https://api.footy-games.com.ar');

const SoloBotPageArg = () => {
  const navigate = useNavigate();
  const [turn, setTurn] = useState('X');
  const [squares, setSquares] = useState(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
  const [winningSquares, setWinningSquares] = useState([]);
  const [score, setScore] = useState({ X: 0, O: 0 });
  const [streak, setStreak] = useState(0);
  const [difficulty, setDifficulty] = useState('medium');
  const [league] = useState('arg');
  const [items, setItems] = useState(Array(6).fill({}));
  const [combos, setCombos] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const { timeLimit } = useContext(TimeLimitContext);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [gameToken, setGameToken] = useState(null);
  const [wrongPickCount, setWrongPickCount] = useState(0);
  const hasAnyData = squares.some(square => square.value !== '' || square.playerName !== '');
  const location = useLocation();
  const variant = location.state?.variant || 'classic';
  const isStealMode = variant === 'steal';
  const isFour12Mode = variant === 'four12';
  const isClassicMode = variant === 'classic';
  // Clásico: el rival es el bot o uno de los personajes de 412, aleatorio por partida o fijo.
  const classicRival = useClassicRival(isClassicMode);
  const { nextRound: nextClassicRival } = classicRival;
  const { stealCap } = useContext(StealCapContext);
  const [consecutiveSkips, setConsecutiveSkips] = useState(0);
  const [stealFlashIdx, setStealFlashIdx] = useState(null);
  // Blindaje: una vez por partida, cada jugador (vos o el bot) puede elegir una casilla que,
  // al jugarla correctamente, queda intocable para siempre (sin importar el tope de jugadas).
  const [shieldUsed, setShieldUsed] = useState({ X: false, O: false });
  const [armedShieldPlayer, setArmedShieldPlayer] = useState(null); // 'X' | null (el bot decide solo)
  // Modo 412: casilla "elegida" este turno y set de índices de `items` (0-5) ya revelados —
  // ver SoloBotPage.js para la explicación completa del patrón.
  const [lockedSquare, setLockedSquare] = useState(null);
  const [revealedItems, setRevealedItems] = useState(() => new Set());
  // Modo 412: en vez del bot genérico juega uno de los 5 personajes, sorteado por ronda
  // (cada reset elige uno DISTINTO al anterior). Fuera de 412 queda null y se ve el bot.
  const [four12Character, setFour12Character] = useState(
    () => (variant === 'four12' ? pickFour12Character() : null)
  );

  const itemMaping = useMemo(() => ({
    0: [items[0], items[3]],
    1: [items[1], items[3]],
    2: [items[2], items[3]],
    3: [items[0], items[4]],
    4: [items[1], items[4]],
    5: [items[2], items[4]],
    6: [items[0], items[5]],
    7: [items[1], items[5]],
    8: [items[2], items[5]],
  }), [items]);

  const squareToItemIndices = (square) => [square % 3, 3 + Math.floor(square / 3)];

  useEffect(() => {
    if (location.state?.mode === 'arg') {
      getSquareCombinationsArg()
        .then((data) => {
          setCombos(data);
          const initialCombination = selectRandomCombination(data);
          setItems(initialCombination);
        })
        .catch((err) => console.error('Error al obtener combinaciones arg:', err));
    }
  }, [location.state]);

  useEffect(() => {
    setTimeRemaining(timeLimit);
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === 0) {
          handleSkipTurn();
          setSelectedPlayer(null);
          return timeLimit;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLimit, turn]);

  useEffect(() => {
    saveGameToken(squares, setGameToken);
  }, [squares]);

  const handleSkipTurn = () => {
    setLockedSquare(null);
    setTurn((prev) => (prev === 'X' ? 'O' : 'X'));
  };

  const handleChangeItems = useCallback(() => {
    if (combos.length > 0) {
      setItems(selectRandomCombination(combos));
    }
  }, [combos]);

  const handlePlayerSelect = (player) => {
    if (isFour12Mode && lockedSquare !== null) {
      attemptPlacement(lockedSquare, player);
      return;
    }
    setSelectedPlayer(player);
  };

  // Extraído de handleClick para poder dispararlo tanto desde un click (Clásico/Robo) como
  // desde elegir un jugador en el buscador (Modo 412, donde la casilla ya está comprometida).
  const attemptPlacement = (square, playerToPlace) => {
    const sq = squares[square];
    const [item1, item2] = itemMaping[square];
    const playedInItem1 = checkPlayer(playerToPlace, item1);
    const playedInItem2 = checkPlayer(playerToPlace, item2);

    if (playedInItem1 && playedInItem2) {
      const wasSteal = isStealMode && sq.value !== '' && sq.value !== turn;
      const wasReinforce = isStealMode && sq.value !== '' && sq.value === turn;
      const stolenFromName = sq.playerName;
      const newClaims = (sq.claims || 0) + 1;
      // Reforzar tu propia casilla (poner otro jugador sobre una que ya era tuya) la blinda para
      // siempre al instante, GRATIS: no gasta el blindaje único del botón.
      const willShield = wasReinforce || (isStealMode && armedShieldPlayer === turn);
      const consumesShieldCharge = willShield && !wasReinforce;
      const newSquares = [...squares];
      newSquares.splice(square, 1, {
        value: turn,
        playerName: playerToPlace.nombre,
        playerImg: playerToPlace.img || null,
        claims: newClaims,
        claimedBy: isStealMode ? [...(sq.claimedBy || []), playerToPlace._id] : (sq.claimedBy || []),
        shielded: willShield ? true : (sq.shielded || false),
      });
      setConsecutiveSkips(0);
      checkForWinner(newSquares);
      setSquares(newSquares);
      setSelectedPlayer(null);
      if (isFour12Mode) setLockedSquare(null);
      if (consumesShieldCharge) {
        setShieldUsed(prev => ({ ...prev, [turn]: true }));
        setArmedShieldPlayer(null);
        if (wasSteal) {
          setStealFlashIdx(square);
          setTimeout(() => setStealFlashIdx(null), 1200);
        }
        toast.success(
          wasSteal
            ? `🛡️🔥 ¡Le robaste la casilla a ${stolenFromName} y la blindaste para siempre!`
            : '🛡️ ¡Casilla blindada para siempre! Nadie va a poder tocarla.',
          {
            position: 'top-right', autoClose: 4000, hideProgressBar: false,
            closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
          }
        );
      } else if (wasReinforce) {
        toast.success('🛡️ ¡Reforzaste tu casilla y quedó blindada para siempre! (No gastó tu blindaje).', {
          position: 'top-right', autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
        });
      } else if (wasSteal) {
        setStealFlashIdx(square);
        setTimeout(() => setStealFlashIdx(null), 1200);
        toast.success(`🔥 ¡Le robaste la casilla a ${stolenFromName}!`, {
          position: 'top-right', autoClose: 3000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
        });
      }
    } else if (isFour12Mode) {
      // Igual que Clásico/Robo: errar pierde el turno. handleSkipTurn() ya limpia
      // lockedSquare, así que el rival (o vos, en tu próximo turno) puede elegir cualquier
      // casilla vacía de nuevo — no queda "trabado" reintentando la misma para siempre.
      toast.warn('Ese jugador no cumple los 2 requisitos de esta casilla. 😔 Perdiste el turno.', {
        position: 'top-right', autoClose: 4000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
      });
      setWrongPickCount(prev => prev + 1);
      setConsecutiveSkips(prev => prev + 1);
      handleSkipTurn();
      setSelectedPlayer(null);
    } else {
      toast.warn('The player does not meet the requirements. 😔You lost the turn.', {
        position: 'top-right', autoClose: 5000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
      });
      setWrongPickCount(prev => prev + 1);
      setConsecutiveSkips(prev => prev + 1);
      handleSkipTurn();
      setSelectedPlayer(null);
    }
  };

  const handleClick = (square) => {
    const sq = squares[square];

    if (isFour12Mode) {
      if (sq.value !== '') return;
      // El humano siempre es 'X' en esta página (vs bot) — sin esto, un click durante el
      // turno del bot (sin buscador ni selectedPlayer que lo frenen, a diferencia de
      // Clásico/Robo) podría "elegir" una casilla fuera de turno.
      if (turn !== 'X') return;
      if (lockedSquare !== null && lockedSquare !== square) {
        toast.info('Primero resolvé la casilla que elegiste (o usá Skip Turn).', {
          position: 'top-right', autoClose: 3000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
        });
        return;
      }
      if (lockedSquare === null) {
        setLockedSquare(square);
        const [colIdx, rowIdx] = squareToItemIndices(square);
        setRevealedItems(prev => new Set(prev).add(colIdx).add(rowIdx));
        return;
      }
      if (selectedPlayer) attemptPlacement(square, selectedPlayer);
      return;
    }

    if (!selectedPlayer) {
      toast.error('Select a player first', {
        position: 'top-right', autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
      });
      return;
    }

    if (isStealMode && sq.value !== '') {
      if (sq.shielded) {
        toast.info('🛡️ Esta casilla está blindada para siempre. Elegí otra.', {
          position: 'top-right', autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
        });
        return; // no pierde el turno, solo avisa
      }
      if (stealCap !== 'unlimited' && (sq.claims || 0) >= stealCap) {
        toast.info(`Esta casilla ya llegó al tope de ${stealCap} jugadas y quedó bloqueada. Elegí otra.`, {
          position: 'top-right', autoClose: 4000, hideProgressBar: false,
          closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
        });
        return; // no pierde el turno, solo avisa
      }
    } else if (!isStealMode && sq.value !== '') {
      return; // clásico: Square ya bloquea el click, resguardo
    }

    if (isStealMode && sq.value !== '' && (sq.claimedBy || []).includes(selectedPlayer._id)) {
      toast.warn('Ese jugador ya se usó en ESTA casilla. Elegí otro (podés usarlo en otra casilla).', {
        position: 'top-right', autoClose: 4000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
      });
      setSelectedPlayer(null); // no pierde el turno, solo tiene que elegir otro jugador
      return;
    }

    attemptPlacement(square, selectedPlayer);
  };

  const reset = useCallback(() => {
    setSquares(Array(9).fill({ value: '', playerName: '', claims: 0, claimedBy: [], shielded: false }));
    setTurn((prev) => (prev === 'X' ? 'O' : 'X'));
    setWinningSquares([]);
    setTimeRemaining(timeLimit);
    setConsecutiveSkips(0);
    setShieldUsed({ X: false, O: false });
    setArmedShieldPlayer(null);
    setLockedSquare(null);
    setRevealedItems(new Set());
    if (isFour12Mode) setFour12Character(prev => pickFour12Character(prev?.id ?? null));
    if (isClassicMode) nextClassicRival();
    handleChangeItems();
  }, [timeLimit, handleChangeItems, isFour12Mode, isClassicMode, nextClassicRival]);

  const endGame = useCallback((result, winPos) => {
    setTurn(null);
    if (result != null) {
      setScore((prev) => ({ ...prev, [result]: prev[result] + 1 }));
      saveStreak(streak, setStreak, result);
    }
    setWinningSquares(winPos);
    setTimeout(reset, 3000);
    savePoints(gameToken, result, difficulty, league, streak, items);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, gameToken, difficulty, league, streak, items]);

  const checkForWinner = useCallback((newSquares) => {
    for (const [a, b, c] of winningPositions) {
      if (
        newSquares[a]?.value !== '' &&
        newSquares[a].value === newSquares[b].value &&
        newSquares[a].value === newSquares[c].value
      ) {
        endGame(newSquares[a].value, [a, b, c]);
        return;
      }
    }
    // Clásico: tablero lleno = empate. Robo: las casillas ocupadas siguen siendo robables, así
    // que el tablero lleno NO implica empate (la salvaguarda de partidas eternas es aparte,
    // ver el useEffect de consecutiveSkips más abajo).
    if (!isStealMode && !newSquares.some(sq => sq.value === '')) {
      endGame(null, Array.from(Array(10).keys()));
      return;
    }
    handleSkipTurn();
  }, [endGame, isStealMode]);

  // Robo y 412: si nadie pudo jugar en 2 vueltas completas (4 turnos salteados seguidos sin
  // ninguna jugada válida), se corta como empate en vez de trabarse para siempre.
  useEffect(() => {
    if ((isStealMode || isFour12Mode) && turn !== null && consecutiveSkips >= 4) {
      endGame(null, Array.from(Array(10).keys()));
    }
  }, [consecutiveSkips, isStealMode, isFour12Mode, turn, endGame]);

  // eslint-disable-next-line no-unused-vars
  const handleClickTitle = () => navigate('/');

  // ── Bot logic ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let timeOutId;

    const fetchBotPlayers = async (item1, item2) => {
      try {
        const customLatinize = (text) =>
          text.split(/(ñ)/gi).map(part =>
            part.toLowerCase() === 'ñ' ? part : latinize(part)
          ).join('');

        const res = await fetch(
          `${backendURL}/players/searchByCriteria?item1=${customLatinize(item1.toLowerCase())}&item2=${customLatinize(item2.toLowerCase())}&league=${league}`
        );
        return await res.json();
      } catch {
        return [];
      }
    };

    const botLogic = async () => {
      const order = pickBotSquareOrder(squares, difficulty, {
        isStealMode, stealCap, botSymbol: 'O', oppSymbol: 'X',
      });

      if (order.length === 0) {
        if (!isStealMode) {
          endGame(null, Array.from(Array(10).keys()));
        } else {
          setConsecutiveSkips(prev => prev + 1);
          handleSkipTurn();
        }
        return;
      }

      const delay = Math.random() * (10000 - 3000) + 3000;
      timeOutId = setTimeout(async () => {
        const achievementKeys = [
          'champions', 'balon_oro', 'europa_league', 'campeon_mundo',
          'eurocopa', 'copa_america', 'jjoo', 'bota_oro', 'goldenboy',
        ];

        for (const chosenSquare of order) {
          const candidateSq = squares[chosenSquare];
          if (candidateSq.shielded) continue; // nunca tocar una casilla blindada
          const [item1, item2] = itemMaping[chosenSquare];
          const rawBotPlayers = await fetchBotPlayers(item1, item2);
          const botPlayers = isStealMode
            ? rawBotPlayers.filter(p => !(candidateSq.claimedBy || []).includes(p._id))
            : rawBotPlayers;

          if (botPlayers.length === 0) continue;

          const playersWithCounts = botPlayers.map(player => ({
            player,
            count: achievementKeys.reduce((sum, key) => sum + (player[key] ? 1 : 0), 0),
          }));

          // Prefer players with an image
          const withImg = playersWithCounts.filter(p => p.player.img);
          const pool = withImg.length > 0 ? withImg : playersWithCounts;

          const aboveFour = pool.filter(p => p.count > 4).map(p => p.player);
          let botPlayer;
          if (aboveFour.length > 0) {
            botPlayer = aboveFour[Math.floor(Math.random() * aboveFour.length)];
          } else {
            const maxCount = Math.max(...pool.map(p => p.count));
            const top = pool.filter(p => p.count === maxCount).map(p => p.player);
            botPlayer = top[Math.floor(Math.random() * top.length)];
          }

          // Modo 412: el bot "clickea" la casilla primero — se revela fila+columna y queda
          // resaltada, igual que cuando lo hace el humano — y recién tras una pausa breve pone
          // al jugador, en vez de aparecer resuelta de golpe.
          if (isFour12Mode) {
            setLockedSquare(chosenSquare);
            const [colIdx, rowIdx] = squareToItemIndices(chosenSquare);
            setRevealedItems(prev => new Set(prev).add(colIdx).add(rowIdx));
            await new Promise(resolve => setTimeout(resolve, 1300));
          }

          const sq = candidateSq;
          const wasSteal = isStealMode && sq.value !== '' && sq.value !== 'O';
          const wasReinforce = isStealMode && sq.value === 'O'; // reforzó su propia casilla
          const stolenFromName = sq.playerName;
          const newSquares = [...squares];
          newSquares.splice(chosenSquare, 1, {
            value: 'O',
            playerName: botPlayer.nombre,
            playerImg: botPlayer.img || null,
            claims: (sq.claims || 0) + 1,
            claimedBy: isStealMode ? [...(sq.claimedBy || []), botPlayer._id] : (sq.claimedBy || []),
            // Reforzar la propia casilla la blinda gratis al instante (no gasta el blindaje único).
            shielded: wasReinforce ? true : (sq.shielded || false),
          });
          const botShields = !wasReinforce && isStealMode && shouldBotShield(
            chosenSquare, newSquares, 'O', difficulty, shieldUsed.O, { oppSymbol: 'X', stealCap }
          );
          if (botShields) newSquares[chosenSquare] = { ...newSquares[chosenSquare], shielded: true };
          setConsecutiveSkips(0);
          checkForWinner(newSquares);
          setSquares(newSquares);
          if (isFour12Mode) setLockedSquare(null); // ya puso al jugador: libera el resaltado
          if (botShields) {
            setShieldUsed(prev => ({ ...prev, O: true }));
            if (wasSteal) {
              setStealFlashIdx(chosenSquare);
              setTimeout(() => setStealFlashIdx(null), 1200);
            }
            toast.info(
              wasSteal
                ? `🛡️🔥 ¡El bot le robó la casilla a ${stolenFromName} y la blindó para siempre!`
                : '🛡️ ¡El bot blindó una casilla para siempre!',
              {
                position: 'top-right', autoClose: 4000, hideProgressBar: false,
                closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
              }
            );
          } else if (wasReinforce) {
            toast.info('🛡️ El bot reforzó su casilla: quedó blindada para siempre (no gastó su blindaje).', {
              position: 'top-right', autoClose: 4000, hideProgressBar: false,
              closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
            });
          } else if (wasSteal) {
            setStealFlashIdx(chosenSquare);
            setTimeout(() => setStealFlashIdx(null), 1200);
            toast.info(`🔥 ¡El bot le robó la casilla a ${stolenFromName}!`, {
              position: 'top-right', autoClose: 3000, hideProgressBar: false,
              closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'dark',
            });
          }
          return;
        }

        if (isStealMode) setConsecutiveSkips(prev => prev + 1);
        handleSkipTurn();
      }, delay);
    };

    if (turn === 'O') botLogic();
    return () => clearTimeout(timeOutId);
  }, [turn, squares, endGame, itemMaping, checkForWinner, difficulty, league, isStealMode, stealCap, shieldUsed.O, isFour12Mode]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <ToastContainer
        position="top-right" autoClose={3000} hideProgressBar={false}
        newestOnTop={false} closeOnClick rtl={false}
        pauseOnFocusLoss draggable pauseOnHover theme="dark"
      />
      <Navbar gamePage />

      <div className="game-page">
        <h1 className="game-title">Footy <span>TicTacToe</span></h1>

        <div className="game-layout">

          {/* ── LEFT panel: Bot character (desktop) ── */}
          <aside className="game-panel game-panel--left">
            <BotCharacter
              turn={turn}
              timeRemaining={timeRemaining}
              score={score}
              squares={squares}
              timeLimit={timeLimit}
              difficulty={difficulty}
              wrongPickTrigger={wrongPickCount}
              character={isFour12Mode ? four12Character : classicRival.character}
              footer={isClassicMode
                ? <RivalPicker choice={classicRival.choice} onChange={classicRival.setChoice} />
                : null}
            />
          </aside>

          {/* ── Center: board ── */}
          <main className="game-center">
            <Board
              winningSquares={winningSquares}
              turn={turn}
              squares={squares}
              onClick={handleClick}
              clubSquares={items}
              handlePlayerSelect={handlePlayerSelect}
              player={turn}
              playerFootball={selectedPlayer}
              mode="bot"
              league={league}
              isStealMode={isStealMode}
              stealCap={stealCap}
              stealFlashSquare={stealFlashIdx}
              isFour12Mode={isFour12Mode}
              lockedSquare={lockedSquare}
              revealedItems={revealedItems}
            />
            {selectedPlayer && <PlayerSelected player={selectedPlayer} />}
          </main>

          {/* ── RIGHT panel: score+timer on top / controls below ── */}
          <aside className="game-panel game-panel--right">
            {/* Top: score + timer + streak */}
            <div className="right-panel-top">
              <ScoreBoard scoreO={score.O} scoreX={score.X} turn={turn} />
              {timeRemaining !== null && (
                <div className="panel-card panel-card--timer">
                  <span className="sidebar-label">Time</span>
                  <CountdownTimer timeRemaining={timeRemaining} />
                </div>
              )}
              {streak > 0 && (
                <div className="panel-card" style={{ alignItems: 'center' }}>
                  <FlameIcon streak={streak} />
                </div>
              )}
            </div>

            <div className="right-panel-divider" />

            {/* Bottom: difficulty + buttons */}
            <div className="right-panel-controls">
              <select
                className="btn-game difficulty-selector"
                value={difficulty}
                onChange={e => changeDifficulty(e.target.value, setDifficulty, streak, setStreak)}
                disabled={hasAnyData}
              >
                <option value="very-easy">Very Easy</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="very-hard">Very Hard</option>
              </select>

              {isStealMode && turn === 'X' && (
                  <button
                      className={`btn-game btn-shield${armedShieldPlayer === 'X' ? ' btn-shield--armed' : ''}`}
                      disabled={shieldUsed.X}
                      onClick={() => setArmedShieldPlayer(prev => prev === 'X' ? null : 'X')}
                  >
                      {shieldUsed.X
                          ? '🛡️ Blindaje ya usado'
                          : armedShieldPlayer === 'X'
                              ? '🛡️ Blindaje armado (cancelar)'
                              : '🛡️ Blindar próxima jugada'}
                  </button>
              )}

              <NextButton
                onClick={reset}
                changeTurn={handleSkipTurn}
                turn={turn}
                mode="bot"
                setStreak={setStreak}
              />
            </div>
          </aside>

          {/* ── Mobile info row (score + timer + difficulty) ── */}
          <div className="mobile-info-row">
            <div className="mobile-score-group">
              <ScoreBoard scoreO={score.O} scoreX={score.X} turn={turn} />
              {timeRemaining !== null && (
                <div className="panel-card panel-card--timer">
                  <span className="sidebar-label">Time</span>
                  <CountdownTimer timeRemaining={timeRemaining} />
                </div>
              )}
              {streak > 0 && <FlameIcon streak={streak} />}
            </div>
            <div className="difficulty-area">
              <span className="difficulty-label">Difficulty</span>
              <select
                className="difficulty-selector"
                value={difficulty}
                onChange={e => changeDifficulty(e.target.value, setDifficulty, streak, setStreak)}
                disabled={hasAnyData}
              >
                <option value="very-easy">Very Easy</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="very-hard">Very Hard</option>
              </select>
            </div>
          </div>

        </div>

        {/* Anuncios: debajo del área de juego y, en pantallas muy anchas, columnas a los
            costados. Todos separados del tablero (ver AdBanner). */}
        <AdBanner placement="game" />
        <AdBanner placement="rail-left" />
        <AdBanner placement="rail-right" />
      </div>
    </div>
  );
};

export default SoloBotPageArg;
